import { useState, useEffect, useMemo } from 'react'
import {
  ArrayInput,
  List,
  NumberInput,
  ReferenceField,
  ReferenceInput,
  SimpleForm,
  SimpleFormIterator,
  TextField,
  useGetIdentity,
  useRecordContext,
  AutocompleteInput,
  DatagridConfigurable,
  IconButtonWithTooltip,
  FunctionField,
  Link,
  useNotify,
} from 'react-admin'
import { FormControl, Select as MUIselect, InputLabel, MenuItem, Box, Typography, Stack } from '@mui/material'

import { getRole } from '../../common/roles'
import { supabaseClient } from '../../supabase'
import DashboardToolbar from './DashboardToolbar'
import { TextFieldCopy } from '../../Design/CustomTextField'
import { FormCard, PaieCardWithIcon } from '../../Design/Cards'

import RefreshIcon from '@mui/icons-material/Refresh'
import LinearProgress from '@mui/material/LinearProgress'
import { filterBlankSpaces } from '../../common/customTextFilter'
import MoneyOffCsredIcon from '@mui/icons-material/MoneyOffCsred'
import AccountBalanceIcon from '@mui/icons-material/AccountBalance'

const monthNamesFR = {
  'janvier': 0,
  'février': 1,
  'mars': 2,
  'avril': 3,
  'mai': 4,
  'juin': 5,
  'juillet': 6,
  'août': 7,
  'septembre': 8,
  'octobre': 9,
  'novembre': 10,
  'décembre': 11
}

const EmptyPaie = ({ isAdmin = false, withPaie = false, setWithPaie }) => (
  <>
    <DashboardToolbar isAdmin={isAdmin} withPaie={withPaie} setWithPaie={setWithPaie} />
    <Stack direction="column" spacing={4} alignItems="center" mt={6}>
      <MoneyOffCsredIcon color="ternary" sx={{ fontSize: '40pt' }} />

      <Typography variant="h6">
        Aucune paie n'est à vérifer pour ce mois.
      </Typography>
    </Stack>
  </>
)

const ExpandPubeur = ({ selectedMonth, isCurrentMonth  }) => {
  const userRecord = useRecordContext()
  const notify = useNotify()
  const [paiePubeurs, setPaiePubeurs] = useState(null)
  const [totalPubeur, setTotalPubeur] = useState(0)

  const fetchPaiePubeurs = async () => {
    if (!userRecord || !selectedMonth) return
  
    try {
      const [monthName, year] = selectedMonth.toLowerCase().split(' ')
      const month = monthNamesFR[monthName]
      
      // For a given month, period is from 15th of PREVIOUS month to 15th of CURRENT month
      const startOfMonth = new Date(Number(year), month - 1, 15)  // 15th of previous month
      const endOfMonth = new Date(Number(year), month, 14) // 14th of current month
  
      const { data, error } = await supabaseClient
        .from('paie_pubeur')
        .select('*')
        .eq('profile_id', userRecord.id)
        .gte('created_at', startOfMonth.toISOString())
        .lt('created_at', new Date(endOfMonth.getTime() + 86400000).toISOString())
  
      if (error) {
        console.error('Error fetching data:', error)
        return
      }
  
      setPaiePubeurs(data)
      const total = data.reduce((acc, pubeur) => acc + (pubeur.amount * pubeur.quantity), 0).toFixed(2)
      setTotalPubeur(total)
    } catch (err) {
      console.error('Error in fetchPaiePubeurs:', err)
    }
  }

  const handleSubmit = async (values) => {
    if (!isCurrentMonth) {
      notify('Vous ne pouvez modifier que les paies du mois en cours', { type: 'warning' })
      return
    }

    const { paie_pubeurs } = values

    const newRows = paie_pubeurs.filter(p => !p.id) // Rows without 'id' are new
    const updatedRows = paie_pubeurs.filter(p => p.id) // Rows with 'id' are updates

    const newValues = newRows.map(pubeur => {
      const { id, created_at, ...cleanPubeur } = pubeur

      return {
        ...cleanPubeur,
        profile_id: userRecord.id,
        manuallyadded: true
      }
    })

    const updateValues = updatedRows.map(pubeur => ({
      ...pubeur,
      profile_id: userRecord.id,
      edited: true
    }))

    const deletedValues = paiePubeurs.filter(pubeur => !paie_pubeurs.find(p => p.id === pubeur.id))

    try {
      if (!userRecord) return
      
      // Insert new rows
      if (newValues.length > 0) {
        const { error } = await supabaseClient
          .from('paie_pubeur')
          .insert(newValues)
        
        if (error) {
          console.error('Error inserting new rows:', error)
        }
      }
      
      // Update existing rows
      if (updateValues.length > 0) {
        for (const row of updateValues) {
          const { id, created_at, ...rest } = row

          const { error } = await supabaseClient
            .from('paie_pubeur')
            .update(rest)
            .eq('id', id)

          if (error) {
            console.error('Error updating row:', error)
            notify('Erreur lors de la mise à jour', { type: 'error' })
          }
        }
      }
      
      // Delete rows
      if (deletedValues.length > 0) {
        const { error } = await supabaseClient
          .from('paie_pubeur')
          .delete()
          .in('id', deletedValues.map(p => p.id))
        
        if (error) {
          console.error('Error deleting rows:', error)
        }
      }
      
      // Refetch the data to update the UI
      fetchPaiePubeurs()
    } catch (err) {
      console.error('Error during submission:', err)
      notify('Une erreur est survenue', { type: 'error' })
    }
  }

  // Fetch paie_pubeurs for the current pubeur when selected month or userRecord changes
  useEffect(() => {
    fetchPaiePubeurs()
  }, [userRecord, selectedMonth])

  return paiePubeurs ? (
    <FormCard>
      <SimpleForm onSubmit={handleSubmit} record={{ paie_pubeurs: paiePubeurs }}>
        <ArrayInput source="paie_pubeurs" label={false}>
          <SimpleFormIterator>
            <ReferenceField source="event_id" reference="events" link={false}>
              <FunctionField
                label=""
                render={event => {
                  const d = event?.date ? new Date(event.date) : null
                  return (
                    <Typography variant="caption">
                      <Link to={`${process.env.REACT_APP_WEBSITE_URL}/events/${event.id}`} target="_blank">
                      {event?.name ?? 'Événement sans nom'}
                      {d ? ` — ${d.toLocaleDateString('fr-FR', { year: 'numeric', month: 'long', day: 'numeric' })}` : ''}
                      </Link>
                    </Typography>
                  )
                }}
              />
            </ReferenceField>

            <Stack direction="row" justifyContent="space-between" sx={{ width: '100%' }}>
              <ReferenceInput source="mission_id" reference="paie_missions">
                <AutocompleteInput
                  optionText="name"
                  label="Mission"
                  sx={{ width: '50%' }}
                  filterToQuery={(searchText) => filterBlankSpaces(searchText)}
                />
              </ReferenceInput>

              <ReferenceInput
                source="unit_id"
                reference="paie_units"
              >
                <AutocompleteInput
                  optionText="name"
                  label="Unité de mesure"
                  sx={{ width: '20%' }}
                  filterToQuery={(searchText) => filterBlankSpaces(searchText)}
                />
              </ReferenceInput>
              
              <NumberInput source="amount" label="Montant" sx={{ width: '13%' }} />
              
              <NumberInput source="quantity" label="Quantité" sx={{ width: '12%' }} />
            </Stack>
          </SimpleFormIterator>
        </ArrayInput>
      </SimpleForm>

      <Typography variant="body1" sx={{ fontWeight: 'bold', textAlign: 'right' }} gutterBottom>
        {`TOTAL BRUT : ${totalPubeur} €`}
        <IconButtonWithTooltip
          label="Rafraîchir"
          onClick={fetchPaiePubeurs}
        >
          <RefreshIcon />
        </IconButtonWithTooltip>
      </Typography>
    </FormCard>
  ) : <LinearProgress />
}

const getPreviousMonths = (count) => {
  const months = []
  const currentDate = new Date()
  
  // Determine if we should start from next month
  const startFromNextMonth = currentDate.getDate() > 14
  const startOffset = startFromNextMonth ? 1 : 0
  
  for (let i = 0; i < count; i++) {
    const monthDate = new Date(
      currentDate.getFullYear(), 
      currentDate.getMonth() + startOffset - i, 
      1
    )
    months.push(monthDate.toLocaleString('fr', { month: 'long', year: 'numeric' }))
  }
  return months
}

const DashboardRespoPaies = () => {
  const { data: identity } = useGetIdentity()
  const isAdmin = identity?.role?.grade === getRole('admin')
  
  const frToEnMonth = (frMonth) => {
    if (!frMonth) return ''

    const [month, year] = frMonth.split(' ')
    
    const monthMap = {
      'janvier': 'January',
      'février': 'February',
      'mars': 'March',
      'avril': 'April',
      'mai': 'May',
      'juin': 'June',
      'juillet': 'July',
      'août': 'August',
      'septembre': 'September',
      'octobre': 'October',
      'novembre': 'November',
      'décembre': 'December'
    }

    return `${monthMap[month.toLowerCase()]} ${year}`
  }

  // Set the default selected month to the current month (in French)
  let currentMonth = new Date().toLocaleString('fr', { month: 'long', year: 'numeric' })
  if (new Date().getDate() > 14) {
    const nextMonth = new Date()
    nextMonth.setMonth(nextMonth.getMonth() + 1)
    currentMonth = nextMonth.toLocaleString('fr', { month: 'long', year: 'numeric' })
  }

  const [month, setMonth] = useState(currentMonth)
  const [withPaie, setWithPaie] = useState(true)
  
  const handleChangeMonth = (event) => {
    setMonth(event.target.value)
  }

  // Check if the selected month is the current month
  const isCurrentMonth = month === currentMonth

  const listFilters = useMemo(() => {
    const monthToDisplay = frToEnMonth(month)

    const baseFilters = {
      enabled: true,
      // 'role_id@neq': ['597c594e-c095-4671-a2fa-bce91f2bf920', 'e7d5673e-9f94-4229-a1b4-bb1f4dcd71de'], // admin, respo
      'role_id@neq': ['597c594e-c095-4671-a2fa-bce91f2bf920'], // admin
    }

    if (withPaie) {
      baseFilters['info_paie@jsonb'] = `{"${monthToDisplay}":"true"}`
    }

    if (!isAdmin) {
      return {
        ...baseFilters,
        center_id: identity?.center?.id,
      }
    }

    return baseFilters
  }, [month, withPaie, isAdmin, identity?.center?.id])

  return (
    <Box sx={{ p: 0, maxWidth: '80vw', margin: 'auto' }}>
      <PaieCardWithIcon
        icon={AccountBalanceIcon}
        title={month.charAt(0).toUpperCase() + month.slice(1)}
        subtitle={isCurrentMonth
          ? `Vous pouvez éditer les paies jusqu'au 14 ${month} à 23h59.`
          : "Archive pour consultation uniquement"}
        colorTitle="#fbc410"
      >
        <div style={{ width: '30%' }}>
          <FormControl variant="outlined" sx={{ m: .5, minWidth: '100%' }}>
            <InputLabel id="monthLabel">Consulter</InputLabel>
            <MUIselect
              labelId="monthLabel"
              id="select-monthLabel"
              value={month}
              variant="outlined"
              onChange={handleChangeMonth}
              label="Consulter"
            >
              {getPreviousMonths(12).map((monthValue) => (
                <MenuItem key={monthValue} value={monthValue}>{monthValue}</MenuItem>
              ))}
            </MUIselect>
          </FormControl>
        </div>
      </PaieCardWithIcon>

      <List
        disableSyncWithLocation
        resource="profiles"
        basePath="/profiles"
        queryOptions={{ meta: { useView: 'paie' } }} // force the use of profiles_view_paie for this list
        perPage={25}
        filter={listFilters}
        exporter={false}
        actions={false}
        sx={{ mt: 2 }}
        empty={
          <EmptyPaie
            isAdmin={isAdmin}
            withPaie="withPaie"
            setWithPaie={setWithPaie}
          />
        }
      >
        <DashboardToolbar isAdmin={isAdmin} withPaie={withPaie} setWithPaie={setWithPaie} />
        <DatagridConfigurable
          bulkActionButtons={false}
          rowClick="expand"
          expand={<ExpandPubeur selectedMonth={month} isCurrentMonth={isCurrentMonth} />}
          expandSingle
        >
          <TextField source="first_name" label="Prénom" />
          <TextField source="last_name" label="Nom" />
          <TextFieldCopy source="id" label="ID" truncated />
          <ReferenceField source="role_id" reference="roles" label="Role" link={false}>
            <TextField source="name" />
          </ReferenceField>
        </DatagridConfigurable>
      </List>
    </Box>
  )
}

export default DashboardRespoPaies
